import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import logoSrc from "@/assets/logo.png";

export default function SignupPage() {
  const [searchParams] = useSearchParams();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const fromStripe = searchParams.get("checkout") === "success";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!fullName.trim()) {
      setError("Please enter your full name.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    setLoading(true);

    const { data, error: signUpError } = await supabase.auth.signUp({
      email: email.trim().toLowerCase(),
      password,
      options: {
        data: { full_name: fullName.trim(), role: "user" },
      },
    });

    if (signUpError) {
      setLoading(false);
      const msg = signUpError.message || "";
      if (
        msg.includes("already registered") ||
        msg.includes("already been registered") ||
        msg.includes("User already registered")
      ) {
        setError(
          "An account with this email already exists. Try signing in instead."
        );
      } else if (msg.includes("valid email")) {
        setError("Please enter a valid email address.");
      } else if (msg.includes("Password should be")) {
        setError("Password must be at least 6 characters.");
      } else {
        setError(msg || "Something went wrong. Please try again.");
      }
      return;
    }

    if (data?.user && !data.user.confirmed_at && data.user.identities?.length === 0) {
      setLoading(false);
      setError("An account with this email already exists. Try signing in instead.");
      return;
    }

    if (!data?.user) {
      setLoading(false);
      setError("Something went wrong. Please try again.");
      return;
    }

    // Try auto-login
    const { error: loginError } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
    });

    setLoading(false);

    if (!loginError) {
      setSuccess(true);
      setTimeout(() => navigate("/dashboard"), 1500);
    } else if (loginError.message?.includes("Email not confirmed")) {
      setSuccess(true);
    } else {
      setSuccess(true);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-4">
        <div className="w-full max-w-md text-center">
          <div className="bg-white rounded-2xl border border-gray-100 p-10">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-emerald-100 mx-auto mb-5">
              <i className="ri-check-line text-3xl text-emerald-600"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Account created!
            </h2>
            <p className="text-gray-500 text-sm leading-relaxed mb-6">
              Welcome to ClickSendAI! Redirecting you to your dashboard...
            </p>
            <Link
              to="/dashboard"
              className="inline-block px-6 py-3 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white font-semibold text-sm hover:opacity-90 transition-opacity whitespace-nowrap cursor-pointer"
            >
              Go to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6 cursor-pointer">
            <img src={logoSrc} alt="ClickSendAI" className="h-10 w-auto object-contain" />
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">Create your account</h1>
          <p className="text-sm text-gray-500 mt-1">
            {fromStripe
              ? "Your trial is active! Now set up your account access."
              : "Access your ClickSendAI dashboard"}
          </p>
        </div>

        {fromStripe && (
          <div className="mb-4 p-4 rounded-xl bg-emerald-50 border border-emerald-200 flex items-start gap-3">
            <i className="ri-checkbox-circle-fill text-emerald-500 text-lg mt-0.5 flex-shrink-0"></i>
            <div>
              <p className="text-sm font-semibold text-emerald-800">
                Stripe checkout complete!
              </p>
              <p className="text-xs text-emerald-700 mt-0.5">
                Use the <strong>same email</strong> you used during checkout to link your subscription.
              </p>
            </div>
          </div>
        )}

        {!fromStripe && (
          <div className="mb-4 p-4 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-3">
            <i className="ri-information-line text-amber-500 text-lg mt-0.5 flex-shrink-0"></i>
            <div>
              <p className="text-xs text-amber-700">
                Starting a new trial?{" "}
                <Link to="/get-started" className="font-semibold underline cursor-pointer">
                  Choose a plan first →
                </Link>
              </p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8">
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-600 text-sm flex items-start gap-2">
              <i className="ri-error-warning-line mt-0.5 shrink-0"></i>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name
              </label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                autoComplete="name"
                className="w-full px-4 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400 transition-all"
                placeholder="Your full name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="w-full px-4 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400 transition-all"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  className="w-full px-4 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400 transition-all pr-10"
                  placeholder="Min 6 characters"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 cursor-pointer"
                >
                  <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                </button>
              </div>
              {password && password.length < 6 && (
                <p className="text-xs text-rose-500 mt-1">
                  Password must be at least 6 characters
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password
              </label>
              <input
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                className="w-full px-4 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400 transition-all"
                placeholder="Confirm your password"
              />
              {confirmPassword && password !== confirmPassword && (
                <p className="text-xs text-rose-500 mt-1">
                  Passwords do not match
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white font-semibold text-sm hover:opacity-90 transition-opacity disabled:opacity-50 cursor-pointer whitespace-nowrap"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Creating account...
                </span>
              ) : (
                "Create Account"
              )}
            </button>
          </form>

          <div className="mt-5 text-center space-y-2">
            <p className="text-sm text-gray-500">
              Already have an account?{" "}
              <Link
                to="/login"
                className="text-violet-600 font-semibold hover:underline cursor-pointer"
              >
                Sign in
              </Link>
            </p>
            <p className="text-xs text-gray-400">
              Starting fresh?{" "}
              <Link
                to="/get-started"
                className="text-violet-500 hover:underline cursor-pointer"
              >
                Choose a plan first
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
